import { Text, Box, PasswordInputProps, Popover, Progress, PasswordInput, Anchor, Group } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";

export function PasswordRequirement({ meets, label }: { meets: boolean; label: string }) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
      component="div"
    >
      {meets ? <IconCheck size={14} /> : <IconX size={14} />}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  { re: /[0-9]/, label: 'Includes number' },
  { re: /[a-z]/, label: 'Includes lowercase letter' },
  { re: /[A-Z]/, label: 'Includes uppercase letter' },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol' },
];

export function getStrength(password: string) {
  let multiplier = password.length > 7 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

export interface PasswordInputWithRequirementsProps extends PasswordInputProps {
  recoverlink?: string;
  usereqpanel?: boolean;
}

export function PasswordInputWithRequirements(props: PasswordInputWithRequirementsProps) {
  props = { usereqpanel: true, ...props };
  const [popoverOpened, setPopoverOpened] = useState(false);
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement key={index} label={requirement.label} meets={requirement.re.test(props.value as string)} />
  ));

  const strength = getStrength(props.value as string);
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  return (
    <Popover opened={popoverOpened && props.usereqpanel} position="bottom" width="target" transitionProps={{ transition: 'pop' }}>
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <Group justify="space-between" mb={5}>
            <Text component="label" htmlFor="password-requirements-input" size={props.size ?? 'md'} fw={500}>
              {props.label}
            </Text>

            {props.recoverlink && <Anchor 
              href={props.recoverlink} 
              pt={2} 
              fw={500} 
              fz="md"
              target="_self"
              >
              Forgot your password?
            </Anchor>}
          </Group>
          <PasswordInput
            id="password-requirements-input"
            withAsterisk
            {...props}
            label={undefined}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement label="Includes at least 8 characters" meets={(props.value as string).length > 7} />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
}